import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Sparkles, Phone, Bot, Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";
import { normalizeBrand } from "@/lib/brand";
import { useLang } from "@/context/LanguageContext";

const STARTERS = [
  "What can ERAM DIGITAL help me build?",
  "I need a generative AI assistant",
  "Help me migrate to the cloud",
  "Do you offer managed IT & staffing?",
];

const WELCOME =
  "Hi, I'm ERA — ERAM DIGITAL's AI assistant. I can help you explore our services, find the right fit, or book a consultation. What are you working on?";

const stripForSpeech = (t) =>
  (t || "")
    .replace(/\*\*/g, "")
    .replace(/[#`_>]/g, "")
    .replace(/\s+/g, " ")
    .trim();

export const EraChat = () => {
  const { t, lang, dir } = useLang();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceOut, setVoiceOut] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const sendRef = useRef(null);
  const audioRef = useRef(null);
  const [speaking, setSpeaking] = useState(false);

  const speechSupported = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const ttsSupported = typeof window !== "undefined" && "speechSynthesis" in window;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  useEffect(() => {
    if (open && inputRef.current) setTimeout(() => inputRef.current?.focus(), 300);
  }, [open]);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch {}
      audioRef.current = null;
    }
    if (ttsSupported) window.speechSynthesis.cancel();
    setSpeaking(false);
  }, [ttsSupported]);

  const browserSpeak = useCallback(
    (text) => {
      if (!ttsSupported) return;
      const synth = window.speechSynthesis;
      synth.cancel();
      const utter = new SpeechSynthesisUtterance(stripForSpeech(text));
      utter.lang = lang === "ar" ? "ar-SA" : "en-US";
      utter.rate = 1.02;
      utter.onend = () => setSpeaking(false);
      setSpeaking(true);
      synth.speak(utter);
    },
    [ttsSupported, lang]
  );

  const speak = useCallback(
    async (text) => {
      if (!voiceOut || !text) return;
      stopAudio();
      try {
        const res = await fetch(`${API}/tts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text, lang }),
        });
        if (!res.ok) throw new Error("tts");
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => {
          URL.revokeObjectURL(url);
          setSpeaking(false);
        };
        setSpeaking(true);
        await audio.play();
      } catch {
        // Premium voice unavailable — fall back to browser speech
        browserSpeak(text);
      }
    },
    [voiceOut, stopAudio, browserSpeak, lang]
  );

  const send = useCallback(
    async (text) => {
      const content = (text ?? input).trim();
      if (!content || streaming) return;
      stopAudio();
      setInput("");
      setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
      setStreaming(true);

      let assistantText = "";
      try {
        const res = await fetch(`${API}/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId, message: content }),
        });
        if (!res.ok || !res.body) throw new Error("Network error");

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n\n");
          buffer = lines.pop() || "";
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const data = line.slice(6);
            if (data === "[DONE]") continue;
            try {
              const parsed = JSON.parse(data);
              if (parsed.session_id) setSessionId((s) => s || parsed.session_id);
              if (parsed.token) {
                assistantText += parsed.token.replace(/\*/g, "");
                setMessages((m) => {
                  const copy = [...m];
                  copy[copy.length - 1] = { role: "assistant", content: assistantText };
                  return copy;
                });
              }
            } catch {
              /* ignore partial chunk parse errors */
            }
          }
        }
        if (assistantText) speak(assistantText);
      } catch (e) {
        const errMsg =
          "Sorry, I hit a connection issue. Please try again, or reach us directly via the contact page.";
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: errMsg };
          return copy;
        });
      } finally {
        setStreaming(false);
      }
    },
    [input, sessionId, streaming, speak, stopAudio]
  );

  sendRef.current = send;

  // Initialise speech recognition once
  useEffect(() => {
    if (!speechSupported) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = false;
    rec.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
      setTimeout(() => sendRef.current?.(transcript), 100);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recognitionRef.current = rec;
    return () => {
      try { rec.abort(); } catch {}
    };
  }, [speechSupported]);

  const toggleMic = () => {
    const rec = recognitionRef.current;
    if (!rec) return;
    if (listening) {
      rec.stop();
      setListening(false);
    } else {
      stopAudio();
      rec.lang = lang === "ar" ? "ar-SA" : "en-US";
      try {
        rec.start();
        setListening(true);
      } catch {
        setListening(false);
      }
    }
  };

  const toggleVoiceOut = () => {
    setVoiceOut((v) => {
      if (v) stopAudio();
      return !v;
    });
  };

  const closePanel = () => {
    stopAudio();
    if (listening && recognitionRef.current) recognitionRef.current.stop();
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const lastAssistantEmpty =
    streaming && messages[messages.length - 1]?.role === "assistant" && messages[messages.length - 1]?.content === "";

  return (
    <>
      <motion.button
        onClick={() => (open ? closePanel() : setOpen(true))}
        className={`fixed bottom-6 z-[60] flex h-16 w-16 items-center justify-center rounded-full gradient-bg text-white shadow-2xl glow-pulse ${
          dir === "rtl" ? "left-6" : "right-6"
        }`}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        aria-label="Open ERA AI assistant"
        data-testid="era-chat-trigger"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X className="h-7 w-7" />
            </motion.span>
          ) : (
            <motion.span key="open" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageSquare className="h-7 w-7" />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className={`fixed bottom-28 z-[60] flex h-[70vh] max-h-[620px] w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-3xl border border-[#E5E7EB] bg-brand-surface shadow-2xl sm:w-[400px] ${
              dir === "rtl" ? "left-4 sm:left-6" : "right-4 sm:right-6"
            }`}
            role="dialog"
            aria-label="ERA AI assistant chat"
            data-testid="era-chat-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 border-b border-[#E5E7EB] bg-[#F3F4F6] px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="relative flex h-10 w-10 items-center justify-center rounded-full gradient-bg">
                  <Bot className="h-5 w-5 text-[#0A0A0A]" />
                  <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-brand-surface bg-emerald-400" />
                </div>
                <div>
                  <p className="font-display text-sm font-semibold text-[#0A0A0A]">{t("era.title")}</p>
                  <p className="flex items-center gap-1 text-xs text-emerald-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> {t("era.online")}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={toggleVoiceOut}
                  className={`rounded-lg p-1.5 transition-colors ${voiceOut ? "text-brand-violet" : "text-[#4B5563] hover:text-[#0A0A0A]"} ${speaking ? "glow-pulse" : ""}`}
                  aria-label={voiceOut ? "Mute ERA voice" : "Enable ERA voice"}
                  title={voiceOut ? "Voice replies on (premium AI voice)" : "Voice replies off"}
                  data-testid="era-voice-toggle"
                >
                  {voiceOut ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
                </button>
                <button onClick={closePanel} className="rounded-lg p-1.5 text-[#4B5563] hover:text-[#0A0A0A]" aria-label="Close chat" data-testid="era-chat-close">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-5" data-testid="era-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`era-message-${i}`}
                    className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user" ? "gradient-bg text-white" : "border border-[#E5E7EB] bg-[#F3F4F6] text-[#374151]"
                    }`}
                  >
                    {normalizeBrand(m.content)}
                    {i === messages.length - 1 && lastAssistantEmpty && (
                      <span className="flex gap-1 py-1">
                        <span className="typing-dot h-2 w-2 rounded-full bg-slate-400" />
                        <span className="typing-dot h-2 w-2 rounded-full bg-slate-400" />
                        <span className="typing-dot h-2 w-2 rounded-full bg-slate-400" />
                      </span>
                    )}
                  </div>
                </div>
              ))}

              {messages.length <= 1 && (
                <div className="pt-2">
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-widest text-[#6B7280]">
                    <Sparkles className="h-3.5 w-3.5" /> {t("era.startersLabel")}
                  </p>
                  <div className="flex flex-col gap-2">
                    {STARTERS.map((s, i) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        className="rounded-xl border border-[#E5E7EB] bg-[#F3F4F6] px-3.5 py-2.5 text-start text-sm text-[#4B5563] transition-colors hover:border-brand-violet/50 hover:text-[#0A0A0A]"
                        data-testid={`era-starter-${i}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Listening indicator */}
            {listening && (
              <div className="border-t border-[#E5E7EB] bg-brand-violet/10 px-4 py-2 text-center text-xs font-medium text-brand-violet" data-testid="era-listening">
                Listening… speak now
              </div>
            )}

            {/* Escalation */}
            <div className="border-t border-[#E5E7EB] px-4 py-2">
              <Link
                to="/contact"
                onClick={closePanel}
                className="flex items-center justify-center gap-2 rounded-xl border border-[#E5E7EB] bg-[#F3F4F6] py-2 text-xs font-medium text-[#4B5563] transition-colors hover:text-[#0A0A0A]"
                data-testid="era-escalate"
              >
                <Phone className="h-3.5 w-3.5" /> {t("cta.talkHuman")} · {t("cta.consult")}
              </Link>
            </div>

            {/* Input */}
            <div className="flex items-end gap-2 border-t border-[#E5E7EB] p-3">
              {speechSupported && (
                <button
                  onClick={toggleMic}
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border transition-colors ${
                    listening ? "border-brand-violet bg-brand-violet/20 text-brand-violet glow-pulse" : "border-[#E5E7EB] bg-[#F3F4F6] text-[#4B5563] hover:text-[#0A0A0A]"
                  }`}
                  aria-label={listening ? "Stop listening" : "Speak to ERA"}
                  data-testid="era-mic"
                >
                  {listening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                </button>
              )}
              <textarea
                ref={inputRef}
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={t("era.placeholder")}
                className="max-h-28 min-h-[44px] flex-1 resize-none rounded-xl border border-[#E5E7EB] bg-[#F3F4F6] px-3.5 py-2.5 text-sm text-[#0A0A0A] placeholder:text-[#6B7280] focus:border-brand-violet focus:outline-none"
                data-testid="era-input"
              />
              <button
                onClick={() => send()}
                disabled={streaming || !input.trim()}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl gradient-bg text-white transition-opacity disabled:opacity-40"
                aria-label={t("era.send")}
                data-testid="era-send"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default EraChat;
